import React, { useEffect, useMemo, useState } from 'react';

import {
  BankOutlined,
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  AdminUserDetail,
  LeadPaymentDetail,
  PatchProfilePaymentResponse,
} from 'models/Lead';

import { LeadService } from 'services/LeadService';

import {
  PaymentAccountValues,
  buildPaymentPatch,
  getPaymentAccountValues,
  getPaymentErrorField,
  isValidAccountNumber,
  maskAccountNumber,
} from 'utils/LeadPaymentHelper';
import { toCatalogOptions } from 'utils/LeadProfileCatalogHelper';

import appI18n from '../../../../config/i18n';
import { LeadPaymentCatalogs } from '../useLeadCatalogs';
import SectionActions from './SectionActions';
import SectionEditFooter from './SectionEditFooter';

const KEY =
  'scenes.leads.components.detail_lead_modal.sections.payment_account';
const SHARED = 'scenes.leads.components.detail_lead_modal.sections.payment';

// Backend codes of this card → message shown under the field.
const UNAVAILABLE: Partial<Record<keyof PaymentAccountValues, string>> = {
  bankId: 'bank_unavailable',
  accountTypeId: 'account_type_unavailable',
  pensionFundId: 'pension_fund_unavailable',
  healthProviderId: 'health_provider_unavailable',
  accountNumber: 'account_number_invalid',
};

interface AccountNumberProps {
  id?: string;
  value?: string | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  editing: boolean;
  onCopied: () => void;
}

/** Read mode: masked number with show/copy; edit mode: plain input. */
function AccountNumberInput({
  id,
  value,
  onChange,
  editing,
  onCopied,
}: AccountNumberProps) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const [shown, setShown] = useState(false);

  if (editing) {
    return (
      <Input
        id={id}
        value={value ?? ''}
        onChange={onChange}
        placeholder={t(`${KEY}.placeholders.account_number`)}
        inputMode="numeric"
      />
    );
  }

  const showLabel = t(
    `${KEY}.buttons.${shown ? 'hide_account_number' : 'show_account_number'}`
  );
  const copyLabel = t(`${KEY}.buttons.copy_account_number`);

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Input
        id={id}
        value={shown ? value ?? '' : maskAccountNumber(value)}
        disabled
      />
      {/* Explicit `disabled` wins over the read-only form: these stay usable. */}
      <Tooltip title={showLabel}>
        <Button
          aria-label={showLabel}
          disabled={!value}
          icon={shown ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          onClick={() => setShown((s) => !s)}
        />
      </Tooltip>
      <Tooltip title={copyLabel}>
        <Button
          aria-label={copyLabel}
          disabled={!value}
          icon={<CopyOutlined />}
          onClick={() => {
            if (!value) return;
            navigator.clipboard?.writeText(value);
            onCopied();
          }}
        />
      </Tooltip>
    </Space.Compact>
  );
}

interface Props {
  detail: AdminUserDetail;
  payment: LeadPaymentDetail;
  catalogs: LeadPaymentCatalogs;
  onUpdated: (next: AdminUserDetail) => void;
}

function PaymentAccountSection({
  detail,
  payment,
  catalogs,
  onUpdated,
}: Props) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    form.setFieldsValue(getPaymentAccountValues(payment));
  }, [payment, form]);

  // The lead's current value is appended when the catalog no longer lists it.
  const bankOptions = useMemo(
    () => toCatalogOptions(catalogs.banks, i18n.language, payment.bank),
    [catalogs.banks, i18n.language, payment.bank]
  );
  const accountTypeOptions = useMemo(
    () =>
      toCatalogOptions(
        catalogs.bankAccountTypes,
        i18n.language,
        payment.accountType
      ),
    [catalogs.bankAccountTypes, i18n.language, payment.accountType]
  );
  const pensionFundOptions = useMemo(
    () =>
      toCatalogOptions(
        catalogs.pensionFunds,
        i18n.language,
        payment.pensionFund
      ),
    [catalogs.pensionFunds, i18n.language, payment.pensionFund]
  );
  // FONASA (public) and the Isapres are listed in separate groups.
  const healthOptions = useMemo(() => {
    const all = toCatalogOptions(
      catalogs.healthProviders,
      i18n.language,
      payment.healthProvider
    );
    const kindOf = (id: number) =>
      catalogs.healthProviders.find((e) => e.id === id)?.kind ??
      (payment.healthProvider?.id === id
        ? payment.healthProvider.kind
        : undefined);
    return [
      {
        label: t(`${KEY}.labels.health_public`),
        options: all.filter((o) => kindOf(o.value) === 'public'),
      },
      {
        label: t(`${KEY}.labels.health_isapre`),
        options: all.filter((o) => kindOf(o.value) !== 'public'),
      },
    ].filter((group) => group.options.length > 0);
  }, [catalogs.healthProviders, i18n.language, payment.healthProvider, t]);

  const handleCancel = () => {
    if (saving) return;
    form.resetFields();
    form.setFieldsValue(getPaymentAccountValues(payment));
    setEditing(false);
  };

  const handleSave = async () => {
    let values: PaymentAccountValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const patch = buildPaymentPatch(getPaymentAccountValues(payment), values);
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }

    setSaving(true);
    const response = await LeadService.updateProfilePayment(
      detail.user.id,
      patch
    );
    setSaving(false);

    if (response.success) {
      message.success(t(`${SHARED}.messages.update_success`));
      onUpdated({
        ...detail,
        payment: (response.data as PatchProfilePaymentResponse).updatedProfile,
      });
      setEditing(false);
      return;
    }

    const error = response.data as ApiErrorData;
    message.error(
      (error.message as string) || t(`${SHARED}.messages.update_failed`),
      3.5
    );
    const field = getPaymentErrorField(error.code) as
      | keyof PaymentAccountValues
      | null;
    if (field && UNAVAILABLE[field]) {
      form.setFields([
        {
          name: field,
          // A rejected catalog id is cleared so it is not sent again.
          value:
            field === 'accountNumber' ? form.getFieldValue(field) : undefined,
          errors: [t(`${KEY}.validations.${UNAVAILABLE[field]}`)],
        },
      ]);
    }
  };

  const fieldsDisabled = !editing || saving;
  const required = (key: string) => ({
    required: editing,
    message: t(`${KEY}.validations.${key}_required`),
  });

  return (
    <Card
      className={
        editing
          ? 'lead-section-card lead-section-card--editing'
          : 'lead-section-card'
      }
      size="small"
      title={
        <Space size={8}>
          <BankOutlined />
          <span>{t(`${KEY}.title`)}</span>
        </Space>
      }
      extra={
        <SectionActions editing={editing} onEdit={() => setEditing(true)} />
      }
    >
      <Form form={form} layout="vertical" disabled={!editing}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="bankId"
              label={t(`${KEY}.labels.bank`)}
              rules={[required('bank')]}
            >
              <Select
                showSearch
                allowClear
                loading={catalogs.banksLoading}
                disabled={fieldsDisabled || catalogs.banksLoading}
                options={bankOptions}
                optionFilterProp="label"
                placeholder={t(`${KEY}.placeholders.bank`)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="accountTypeId"
              label={t(`${KEY}.labels.account_type`)}
              rules={[required('account_type')]}
            >
              <Select
                allowClear
                loading={catalogs.bankAccountTypesLoading}
                disabled={fieldsDisabled || catalogs.bankAccountTypesLoading}
                options={accountTypeOptions}
                placeholder={t(`${KEY}.placeholders.account_type`)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="accountNumber"
              label={t(`${KEY}.labels.account_number`)}
              extra={editing ? t(`${KEY}.help.account_number`) : undefined}
              rules={[
                required('account_number'),
                {
                  validator: (_, value) =>
                    isValidAccountNumber(value)
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            t(`${KEY}.validations.account_number_invalid`)
                          )
                        ),
                },
              ]}
            >
              <AccountNumberInput
                editing={editing}
                onCopied={() =>
                  message.success(t(`${KEY}.messages.account_number_copied`))
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="pensionFundId"
              label={t(`${KEY}.labels.pension_fund`)}
              rules={[required('pension_fund')]}
            >
              <Select
                allowClear
                loading={catalogs.pensionFundsLoading}
                disabled={fieldsDisabled || catalogs.pensionFundsLoading}
                options={pensionFundOptions}
                placeholder={t(`${KEY}.placeholders.pension_fund`)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="healthProviderId"
              label={t(`${KEY}.labels.health_provider`)}
              rules={[required('health_provider')]}
            >
              <Select
                allowClear
                loading={catalogs.healthProvidersLoading}
                disabled={fieldsDisabled || catalogs.healthProvidersLoading}
                options={healthOptions}
                placeholder={t(`${KEY}.placeholders.health_provider`)}
              />
            </Form.Item>
          </Col>
        </Row>

        {editing && (
          <SectionEditFooter
            saving={saving}
            onCancel={handleCancel}
            onSave={handleSave}
          />
        )}
      </Form>
    </Card>
  );
}

export default PaymentAccountSection;
